import { isResource, resolveId } from '@medplum/core'
import type {
  Annotation,
  CarePlan,
  CodeableConcept,
  Condition,
  Goal,
  Patient,
  Practitioner,
  Reference,
} from '@medplum/fhirtypes'
import { addDays } from 'date-fns'
import { sortBy } from 'lodash-es'
import { match, P } from 'ts-pattern'
import { codeFromCodeableConcept } from '#src/codeableConcept.ts'
import { CARE_PLAN_SORT_ORDER } from '#src/codeSystems.ts'
import { Extension } from '#src/decorators.ts'
import { ExtendedResource } from '#src/ExtendedResource.ts'
import { invariant } from '#src/invariant.ts'
import {
  CarePlanState,
  type CarePlanStatus,
} from '#src/models/care-plans/constants.ts'
import { hasReference, isOfType, now } from '#src/utils.ts'

interface CarePlanOrderAndIntent {
  status: CarePlan['status']
  intent: CarePlan['intent'] | undefined
}

export class BasePlan extends ExtendedResource<CarePlan> {
  @Extension({
    url: CARE_PLAN_SORT_ORDER,
    kind: 'valueDecimal',
  })
  sortOrder?: number

  static readonly RESOURCE_TYPE = 'CarePlan'

  static isInstance(value: unknown): value is CarePlan | BasePlan {
    return isResource(value) && isOfType(value, 'CarePlan')
  }

  constructor(data: Omit<CarePlan, 'resourceType' | 'intent' | 'status'>) {
    super({
      created: now(),
      status: 'draft',
      intent: 'plan',
      ...data,
    })
  }

  get title(): string {
    return this.resource.title ?? ''
  }

  set title(title) {
    this.resource.title = title
  }

  get description(): string {
    return this.resource.description ?? ''
  }

  set description(description) {
    this.resource.description = description
  }

  get status(): CarePlanStatus {
    return this.resource.status as CarePlanStatus
  }

  set status(status) {
    this.resource.status = status
  }

  get isEditable(): boolean {
    return this.status === 'draft'
  }

  get createdDate(): Date {
    invariant(this.resource.created, 'Care plan must have a created date')
    return new Date(this.resource.created)
  }

  get created(): string | undefined {
    return this.resource.created
  }

  get categoryCodes(): string[] {
    return this.categories.map((c) => codeFromCodeableConcept(c) ?? '')
  }

  get categories(): CodeableConcept[] {
    return this.resource.category ?? []
  }

  get contributors(): Reference<Practitioner>[] {
    return (
      this.resource.contributor?.filter((c): c is Reference<Practitioner> =>
        isOfType(c, 'Practitioner')
      ) ?? []
    )
  }

  get dueDate(): Date {
    return addDays(this.createdDate, 30)
  }

  get startDate(): Date | undefined {
    return this.resource.period?.start
      ? new Date(this.resource.period.start)
      : undefined
  }

  set startDate(startDate: Date) {
    this.resource.period = {
      start: startDate.toISOString(),
      end: addDays(startDate, 180).toISOString(),
    }
  }

  get endDate(): Date | undefined {
    return this.resource.period?.end
      ? new Date(this.resource.period.end)
      : undefined
  }

  get patientSubject(): Reference<Patient> {
    return this.resource.subject as Reference<Patient>
  }

  get conditions(): Reference<Condition>[] {
    return this.resource.addresses ?? []
  }

  get conditionIds(): string[] {
    return this.conditions.map((c) => resolveId(c) ?? '')
  }

  addCondition(condition: Reference<Condition>): void {
    this.resource.addresses ??= []
    this.resource.addresses.push(condition)
  }

  hasCondition(condition: Reference<Condition>): boolean {
    return this.conditions.some((c) => hasReference(c, condition))
  }

  removeCondition(condition: Reference<Condition>): void {
    if (this.hasCondition(condition)) {
      this.resource.addresses = this.conditions.filter(
        (c) => !hasReference(c, condition)
      )
    }
  }

  get goals(): Reference<Goal>[] {
    return this.resource.goal ?? []
  }

  get goalIds(): string[] {
    return this.goals.map((g) => resolveId(g) ?? '')
  }

  addGoal(goal: Reference<Goal>): void {
    this.resource.goal ??= []
    this.resource.goal.push(goal)
  }

  get notes(): Annotation[] {
    return this.resource.note ?? []
  }

  get notesSummary(): {
    time: string | undefined
    text: string
    authorId: string | undefined
  }[] {
    return this.notes.map((n) => ({
      time: n.time,
      text: n.text,
      authorId: resolveId(n.authorReference),
    }))
  }

  removeGoal(goal: Reference<Goal>): void {
    this.resource.goal = this.resource.goal?.filter(
      (g) => !hasReference(g, goal)
    )
  }

  get state(): CarePlanState {
    return BasePlan.orderAndIntentToState(this.status, this.resource.intent)
  }

  get isIncomplete(): boolean {
    return (
      this.state === CarePlanState.Draft ||
      this.state === CarePlanState.PendingSignatures
    )
  }

  get isSignedOff(): boolean {
    return this.state === CarePlanState.SignedOff
  }

  get isActive(): boolean {
    return this.state === CarePlanState.Active
  }

  set state(state: CarePlanState) {
    const { status, intent } = BasePlan.stateToOrderAndIntent(state)

    this.resource.status = status
    if (intent !== undefined) {
      this.resource.intent = intent
    }
  }

  static orderAndIntentToState(
    status: CarePlan['status'],
    intent: CarePlan['intent']
  ): CarePlanState {
    const state = match<
      [CarePlan['status'], CarePlan['intent']],
      CarePlanState
    >([status, intent])
      .with(['draft', 'plan'], () => CarePlanState.Draft)
      .with(['on-hold', 'plan'], () => CarePlanState.PendingSignatures)
      .with(['on-hold', 'order'], () => CarePlanState.SignedOff)
      .with(['active', 'plan'], () => CarePlanState.Active)
      .with(['completed', 'order'], () => CarePlanState.Closed)
      .with(['revoked', P._], () => CarePlanState.Voided)
      .with(['entered-in-error', P._], () => CarePlanState.Deleted)
      .with([P._, P._], () => CarePlanState.Unknown)
      .exhaustive()
    return state
  }

  static stateToOrderAndIntent(state: CarePlanState): CarePlanOrderAndIntent {
    const [status, intent] = match<
      CarePlanState,
      [CarePlan['status'], CarePlan['intent'] | undefined]
    >(state)
      .with(CarePlanState.Draft, () => ['draft', 'plan'])
      .with(CarePlanState.PendingSignatures, () => ['on-hold', 'plan'])
      .with(CarePlanState.SignedOff, () => ['on-hold', 'order'])
      .with(CarePlanState.Active, () => ['active', 'plan'])
      .with(CarePlanState.Closed, () => ['completed', 'order'])
      .with(CarePlanState.Voided, () => ['revoked', undefined])
      .with(CarePlanState.Deleted, () => ['entered-in-error', undefined])
      .with(CarePlanState.Unknown, () => ['unknown', undefined])
      .exhaustive()
    return { status, intent }
  }

  protected getExportFields() {
    return {
      id: this.id,
      title: this.title,
      description: this.description,
      status: this.status,
      created: this.created,
      createdDate: this.createdDate,
      startDate: this.startDate,
      endDate: this.endDate,
      patientId: resolveId(this.patientSubject),
      conditionIds: this.conditionIds,
      goalIds: this.goalIds,
      notes: this.notes,
      notesSummary: this.notesSummary,
      category: this.categoryCodes,
      parentId: this.parentId,
    }
  }

  static calculateMoveUpOrder(
    orders: number[],
    currentIndex: number
  ): number | undefined {
    if (currentIndex <= 0 || currentIndex >= orders.length) {
      return undefined
    }

    const prevOrder = orders[currentIndex - 1]
    const prevPrevOrder =
      currentIndex > 1 ? orders[currentIndex - 2] : prevOrder - 1

    return prevPrevOrder + (prevOrder - prevPrevOrder) / 2
  }

  static calculateMoveDownOrder(
    orders: number[],
    currentIndex: number
  ): number | undefined {
    if (currentIndex === -1 || currentIndex >= orders.length - 1) {
      return undefined
    }

    const nextOrder = orders[currentIndex + 1]
    const nextNextOrder =
      currentIndex < orders.length - 2
        ? orders[currentIndex + 2]
        : nextOrder + 1

    return nextOrder + (nextNextOrder - nextOrder) / 2
  }

  moveUp(items: BasePlan[]): void {
    const sortedItems = sortBy(items, (item) => item.sortOrder ?? 0)
    const currentIndex = sortedItems.findIndex((item) => item.id === this.id)
    const orders = sortedItems.map((item) => item.sortOrder ?? 0)

    const newOrder = BasePlan.calculateMoveUpOrder(orders, currentIndex)
    if (newOrder !== undefined) {
      this.sortOrder = newOrder
    }
  }

  moveDown(items: BasePlan[]): void {
    const sortedItems = sortBy(items, (item) => item.sortOrder ?? 0)
    const currentIndex = sortedItems.findIndex((item) => item.id === this.id)
    const orders = sortedItems.map((item) => item.sortOrder ?? 0)

    const newOrder = BasePlan.calculateMoveDownOrder(orders, currentIndex)
    if (newOrder !== undefined) {
      this.sortOrder = newOrder
    }
  }

  get parentId(): string | undefined {
    const reference = this.resource.partOf?.[0]
    return reference && resolveId(reference)
  }
}
